<template>
  <form @submit.prevent="saveSensor" ref="addVirtualSensorForm">
    <div class="modal" tabindex="-1" role="dialog" backdrop="static" id="addVirtualSensorModal" @keyup.esc="onEsc($event)">
      <div class="modal-dialog" role="document">
        <div class="modal-content">
          <div class="modal-header header">
            <h3 class="modal-title">{{ $t('addVirtualSensorModal.title') }}</h3>
            <button type="button" class="btn-close btn-close-white" @click="close" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <div class="form">
              <div v-show="errorMessage" class="alert alert-danger failed">{{ errorMessage }}</div>
              <div v-if="parentFacility" class="form-group">
                <label>{{ $t('addVirtualSensorModal.facilityLabel') }}</label>
                <div class="blue-text">{{ parentFacility.name }}</div>
              </div>
              <div class="form-group">
                <label for="virtualSensorNameInput">{{ $t('addVirtualSensorModal.nameInput') }}</label>
                <input type="text" class="form-control" :class="{ 'is-invalid': v$.sensor.name.$dirty && v$.sensor.name.$error }" id="virtualSensorNameInput" v-model.trim="sensor.name" :placeholder="$t('addVirtualSensorModal.namePlaceholder')" maxlength="50" />
                <div class="invalid-feedback" v-if="v$.sensor.name.$dirty">
                  <div class="error" v-if="!v$.sensor.name.required">{{ $t('addVirtualSensorModal.nameRequired') }}</div>
                  <div class="error" v-if="!v$.sensor.name.maxLength">{{ $t('addVirtualSensorModal.nameMaxLength', { max: 50 }) }}</div>
                </div>
              </div>
              <div class="form-group">
                <label for="virtualSensorTypeSelect">{{ $t('addVirtualSensorModal.typeInput') }}</label>
                <select class="form-control" id="virtualSensorTypeSelect" v-model="sensor.type">
                  <option v-for="t in sensorTypes" :value="t" :key="t">
                    {{ sensorTypeLabel(t) }}
                  </option>
                </select>
              </div>
              <div class="form-group">
                <label for="virtualSensorStatusinfoInput">{{ $t('addVirtualSensorModal.statusinfoInput') }}</label>
                <textarea v-autoresize rows="1" class="form-control" id="virtualSensorStatusinfoInput" v-model.trim="sensor.statusinfo" :placeholder="$t('addVirtualSensorModal.statusinfoPlaceholder')" maxlength="255"></textarea>
              </div>
              <div class="form-group">
                <label for="virtualSensorExternalidInput">{{ $t('addVirtualSensorModal.externalidInput') }}</label>
                <input type="text" class="form-control" id="virtualSensorExternalidInput" v-model.trim="sensor.externalid" :placeholder="$t('addVirtualSensorModal.externalidPlaceholder')" maxlength="64" />
              </div>
              <div class="form-group">
                <label for="virtualSensorMainInput">{{ $t('addVirtualSensorModal.mainInput') }}</label>
                &nbsp;&nbsp;<input type="checkbox" id="virtualSensorMainInput" v-model="sensor.main">
              </div>
              <div class="form-group">
                <label for="virtualSensorEnabledInput">{{ $t('addVirtualSensorModal.enabledInput') }}</label>
                &nbsp;&nbsp;<input type="checkbox" id="virtualSensorEnabledInput" v-model="sensor.enabled">
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="submit" class="btn btn-primary">{{ $t('addVirtualSensorModal.buttonAdd') }}</button>
            <button type="button" class="btn btn-secondary" @click="close">{{ $t('addVirtualSensorModal.close') }}</button>
          </div>
        </div>
      </div>
    </div>
  </form>
</template>

<script>
import { Modal } from 'bootstrap'
import { required, maxLength } from '@vuelidate/validators'
import { useVuelidate } from '@vuelidate/core'
import cdcsensorService from '@/services/cdcsensors'

export default {
  setup () { return { v$: useVuelidate() } },
  name: 'AddVirtualSensorModal',
  props: {
    parentFacility: {
      type: Object,
      required: true
    },
    modalOpened: {
      type: String,
      required: true
    }
  },
  emits: ['modalClosed'],
  data () {
    return {
      sensor: this.emptySensor(),
      errorMessage: '',
      sensorTypes: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
      anyChanges: false
    }
  },
  validations: {
    sensor: {
      name: {
        required,
        maxLength: maxLength(50)
      }
    }
  },
  watch: {
    modalOpened () {
      this.resetForm()
      this.$nextTick(() => {
        document.getElementById('virtualSensorNameInput')?.focus()
      })
    }
  },
  methods: {
    emptySensor () {
      return {
        name: '',
        type: 0,
        statusinfo: '',
        externalid: '',
        main: false,
        enabled: true,
        factor: 0
      }
    },
    resetForm () {
      this.sensor = this.emptySensor()
      this.errorMessage = ''
      this.anyChanges = false
      this.v$.$reset()
    },
    saveSensor () {
      this.v$.$touch()
      if (this.v$.$invalid || !this.parentFacility || !this.parentFacility.id) {
        return
      }
      cdcsensorService.addVirtualSensor(this.parentFacility.id, this.sensor).then(() => {
        this.anyChanges = true
        this.close()
      }).catch(error => {
        this.errorMessage = this.localizedBackendMessage(error.message)
      })
    },
    onEsc () {
      this.close()
    },
    close () {
      this.$emit('modalClosed', this.anyChanges)
      Modal.getInstance(document.getElementById('addVirtualSensorModal'))?.hide()
    },
    localizedBackendMessage (errorMessage) {
      return this.$t('backend.' + errorMessage)
    },
    sensorTypeLabel (type) {
      return type >= 0 ? this.$t('sensorType.' + type) : '? ' + type
    }
  }
}
</script>

<style lang="scss" scoped>
.modal {
  .modal-dialog {
    width: 400px;
  }
  .blue-text {
    color: #0069d9;
    font-weight: 700;
  }
}
</style>
